import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, LifeBuoy, RotateCcw, Bot, Sparkles, Headset, MessageSquare } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";

const RETURN_REASONS = ["Damaged or defective", "Wrong item received", "Not as described", "Quality not satisfactory", "Other"];
const ICON_MAP = { "message-circle": MessageCircle, "message-square": MessageSquare, bot: Bot, sparkles: Sparkles, headset: Headset, "life-buoy": LifeBuoy };

// Treat `•` bullets (common in the Knowledge base) as real markdown list items.
const normalizeBullets = (t) => (t || "").replace(/^[ \t]*•[ \t]+/gm, "- ");

// Renders a small, SAFE subset of markdown for Sia's bubbles: bold/italic, http(s)
// links (new tab, sanitized), bullet/numbered lists and line breaks. No raw HTML.
const MD_COMPONENTS = {
  a: (props) => <a {...props} target="_blank" rel="noopener noreferrer nofollow" className="font-medium text-blue-600 underline underline-offset-2 break-words" />,
  ul: (props) => <ul {...props} className="my-1 list-disc space-y-0.5 pl-5" />,
  ol: (props) => <ol {...props} className="my-1 list-decimal space-y-0.5 pl-5" />,
  li: (props) => <li {...props} className="marker:text-slate-400" />,
  p: (props) => <p {...props} className="mb-2 last:mb-0" />,
  strong: (props) => <strong {...props} className="font-semibold" />,
  h1: (props) => <p {...props} className="mb-2 font-semibold last:mb-0" />,
  h2: (props) => <p {...props} className="mb-2 font-semibold last:mb-0" />,
  h3: (props) => <p {...props} className="mb-2 font-semibold last:mb-0" />,
};

function SiaText({ text }) {
  return (
    <div className="[&_a]:break-words" data-testid="sia-markdown">
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={MD_COMPONENTS}>
        {normalizeBullets(text)}
      </ReactMarkdown>
    </div>
  );
}

// On-site assistant. Appears only when the server `ai_chatbot` flag is ON.
// When `order_aware_chatbot` is ON and a customer is signed in, it can answer about their
// own orders, deep-link a support ticket to the order, and draft a return request in-chat.
export default function AiAssistantLauncher() {
  const loc = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCustomer = user?.role === "customer";
  const [on, setOn] = useState(false);
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", text: "Hi! I'm the Suffle Assistant. Ask me about products, shipping, returns or how ordering works." }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [session, setSession] = useState(null);
  const [canHandoff, setCanHandoff] = useState(false);
  const [handoff, setHandoff] = useState(false);
  const [handoffOrder, setHandoffOrder] = useState(null);
  const [showReturn, setShowReturn] = useState(false);
  const [retReason, setRetReason] = useState(RETURN_REASONS[0]);
  const [retDesc, setRetDesc] = useState("");
  const [retDone, setRetDone] = useState(false);
  const endRef = useRef(null);
  const nudged = useRef({});
  const [brand, setBrand] = useState(null);
  const [proactive, setProactive] = useState(false);
  const [ticketCreated, setTicketCreated] = useState(false);
  const [greetingSeen, setGreetingSeen] = useState(() => !!localStorage.getItem("suffle_greeting_seen"));
  const variant = useRef(null);
  const convoTracked = useRef(false);

  const trackAb = (type) => { if (variant.current) api.post("/control/public/chatbot-ab/event", { variant: variant.current, type }).catch(() => {}); };

  useEffect(() => {
    api.get("/control/public/feature-flags").then((r) => setOn(!!r.data.flags?.ai_chatbot)).catch(() => {});
    api.get("/control/public/chatbot-branding").then((r) => {
      let b = r.data;
      if (r.data?.ab?.active) {
        let v = localStorage.getItem("suffle_ab_variant");
        if (v !== "a" && v !== "b") { v = Math.random() < 0.5 ? "a" : "b"; localStorage.setItem("suffle_ab_variant", v); }
        variant.current = v;
        b = { ...r.data, ...r.data.ab[v], situations: r.data.situations || [] };
      } else {
        localStorage.removeItem("suffle_ab_variant");
      }
      setBrand(b);
      if (b?.greeting) setMsgs((m) => (m.length === 1 && m[0].role === "assistant" ? [{ role: "assistant", text: b.greeting }] : m));
    }).catch(() => {});
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open, showReturn]);
  useEffect(() => { if (isCustomer) setCanHandoff(true); }, [isCustomer]);
  useEffect(() => { if (open && !greetingSeen) { localStorage.setItem("suffle_greeting_seen", "1"); setGreetingSeen(true); } }, [open, greetingSeen]);

  const path = loc.pathname;
  const hidden = /^\/(admin|agent|login|register)/.test(path);
  const onOrderPage = /^\/app\/(orders|shop-orders|custom-orders|returns)/.test(path);
  useEffect(() => {
    if (!on || hidden || !isCustomer || !onOrderPage || nudged.current[path]) return;
    const t = setTimeout(() => {
      if (nudged.current[path]) return;
      nudged.current[path] = true;
      setCanHandoff(true);
      setProactive(true);
      setMsgs((m) => [...m, { role: "assistant", text: "Need a hand with this order? I can check its status, explain returns and refunds, or connect you with our support team." }]);
      setOpen(true);
    }, 15000);
    return () => clearTimeout(t);
  }, [on, hidden, isCustomer, onOrderPage, path]);

  if (!on || hidden) return null;

  const resolveImg = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
  const onProductPage = /^\/product\//.test(path);
  const trig = { resting: !open, open, proactive, greeting_unseen: !greetingSeen,
                 order_page: onOrderPage, product_page: onProductPage, handoff: ticketCreated };
  const matchedSit = (brand?.situations || []).find((s) => s.avatar_url && trig[s.trigger]);
  const launcherImg = resolveImg(matchedSit?.avatar_url || brand?.avatar_url || "");

  const returnEligible = handoffOrder && !handoff && ["delivered", "completed"].includes(handoffOrder.status);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setSending(true);
    if (!convoTracked.current) { convoTracked.current = true; trackAb("convo"); }
    try {
      const r = await api.post("/control/public/assistant", { message: text, session_id: session, variant: variant.current });
      setSession(r.data.session_id);
      if (r.data.can_handoff) setCanHandoff(true);
      if (r.data.handoff_order) setHandoffOrder(r.data.handoff_order);
      setMsgs((m) => [...m, { role: "assistant", text: r.data.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: "Sorry, I'm having trouble right now. Please try again in a moment." }]);
    } finally { setSending(false); }
  };

  const talkToHuman = async () => {
    if (handoff || sending) return;
    setSending(true);
    const transcript = msgs.map((m) => `${m.role === "user" ? "Shopper" : "Assistant"}: ${m.text}`).join("\n").slice(0, 4800) || "Shopper requested help from the chat assistant.";
    const linked = handoffOrder?.order_number ? ` (order ${handoffOrder.order_number})` : "";
    try {
      const r = await api.post("/support/tickets", {
        category: handoffOrder ? "Orders" : "Other",
        subject: `Help requested from chat assistant${linked}`,
        description: transcript,
        context_type: handoffOrder ? "order" : "general",
        suborder_id: handoffOrder?.suborder_id || null,
      });
      setHandoff(true);
      setTicketCreated(true);
      trackAb("handoff");
      setMsgs((m) => [...m, { role: "assistant", text: `I've connected you with our support team — ticket ${r.data.ticket_code} is open${linked}. They'll reply by email and in your Notifications. Tap below to track it.` }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: `I couldn't open a support ticket (${formatApiError(e?.response?.data?.detail)}). You can raise one from Help & Support.` }]);
    } finally { setSending(false); }
  };

  const submitReturn = async () => {
    if (sending || !handoffOrder) return;
    setSending(true);
    const desc = retDesc.trim() || retReason;
    try {
      await api.post(`/orders/${handoffOrder.suborder_id}/return`, { reason: retReason, description: desc });
      setRetDone(true); setShowReturn(false);
      setMsgs((m) => [...m, { role: "assistant", text: `Your return request for order ${handoffOrder.order_number} has been submitted (${retReason}). Important: an uncut open-box (unboxing) video recorded while unsealing the parcel is required to approve any return or refund. Track it under Returns & Refunds.` }]);
    } catch (e) {
      setShowReturn(false);
      const code = e?.response?.status;
      const text = (code === 404 || code === 400)
        ? "This order can't be returned through chat (it may be a custom order or past its return window). Tap 'Talk to a human' and our team will help."
        : `I couldn't submit the return — ${formatApiError(e?.response?.data?.detail)}. You can still raise it from Returns & Refunds.`;
      setMsgs((m) => [...m, { role: "assistant", text }]);
    } finally { setSending(false); }
  };

  return (
    <div className="fixed bottom-5 right-5 z-[70]" data-testid="ai-assistant-launcher">
      {open && (
        <div className="mb-3 flex h-[34rem] w-[30rem] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl" data-testid="ai-assistant-panel">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <span className="flex items-center gap-2 text-sm font-bold text-slate-800" data-testid="ai-panel-title">
              {launcherImg && <img src={launcherImg} alt="" data-testid="ai-panel-avatar" className="h-7 w-7 rounded-full object-cover" />}
              {brand?.name || "Suffle Assistant"} <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">Beta</span>
            </span>
            <button onClick={() => setOpen(false)} data-testid="ai-assistant-close" className="rounded-full p-1 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
          </div>
          <div className="flex-1 space-y-2.5 overflow-y-auto px-3 py-3">
            {msgs.map((m, i) => (
              <div key={i} className={`flex items-end gap-1.5 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role !== "user" && launcherImg && <img src={launcherImg} alt="" data-testid={`ai-msg-avatar-${i}`} className="h-6 w-6 shrink-0 rounded-full object-cover" />}
                <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-base leading-relaxed ${m.role === "user" ? "whitespace-pre-wrap brand-gradient-bg text-white" : "bg-slate-100 text-slate-700"}`}>
                  {m.role === "user" ? m.text : <SiaText text={m.text} />}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex items-end justify-start gap-1.5">
                {launcherImg && <img src={launcherImg} alt="" data-testid="ai-typing-avatar" className="h-6 w-6 shrink-0 rounded-full object-cover" />}
                <div className="rounded-2xl bg-slate-100 px-4 py-3" data-testid="ai-typing-indicator" aria-label="Sia is typing">
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 animate-bounce rounded-full bg-blue-500 [animation-delay:-0.3s]" />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-violet-500 [animation-delay:-0.15s]" />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-fuchsia-500" />
                  </span>
                </div>
              </div>
            )}
            {showReturn && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="ai-return-form">
                <p className="mb-2 text-xs font-semibold text-slate-700">Draft a return for order {handoffOrder?.order_number}</p>
                <select value={retReason} onChange={(e) => setRetReason(e.target.value)} data-testid="ai-return-reason" className="mb-2 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs outline-none">
                  {RETURN_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <textarea value={retDesc} onChange={(e) => setRetDesc(e.target.value)} placeholder="Add a few details (optional)" rows={2} data-testid="ai-return-desc" className="mb-2 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs outline-none" />
                <p className="mb-2 rounded-lg bg-amber-50 px-2 py-1.5 text-[11px] leading-snug text-amber-700">An uncut open-box (unboxing) video, recorded while unsealing the parcel, is required to approve any return or refund.</p>
                <div className="flex gap-2">
                  <button onClick={submitReturn} disabled={sending} data-testid="ai-return-submit" className="flex-1 rounded-full brand-gradient-bg py-1.5 text-xs font-semibold text-white disabled:opacity-50">Submit return</button>
                  <button onClick={() => setShowReturn(false)} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-500">Cancel</button>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          {(canHandoff || returnEligible) && (
            <div className="space-y-1.5 border-t border-slate-100 px-2.5 pt-2">
              {returnEligible && !showReturn && (
                <button onClick={() => setShowReturn(true)} data-testid="ai-assistant-draft-return" className="flex w-full items-center justify-center gap-1.5 rounded-full border border-slate-200 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50">
                  <RotateCcw className="h-3.5 w-3.5" /> Draft a return request for order {handoffOrder.order_number}
                </button>
              )}
              {canHandoff && (handoff ? (
                <button onClick={() => { setOpen(false); navigate("/app/support"); }} data-testid="ai-assistant-view-ticket" className="flex w-full items-center justify-center gap-1.5 rounded-full bg-emerald-600 py-2 text-xs font-semibold text-white hover:bg-emerald-700">
                  <LifeBuoy className="h-3.5 w-3.5" /> Track my support ticket
                </button>
              ) : (
                <button onClick={talkToHuman} disabled={sending} data-testid="ai-assistant-handoff" className="flex w-full items-center justify-center gap-1.5 rounded-full border border-slate-200 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                  <LifeBuoy className="h-3.5 w-3.5" /> {handoffOrder?.order_number ? `Talk to a human about order ${handoffOrder.order_number}` : "Talk to a human — open a support ticket"}
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-1.5 border-t border-slate-100 px-2.5 pt-2" data-testid="sia-quick-chips">
            {[["Send a parcel", "/services?travel=logistics&travel_type=parcel"], ["Track parcel", "/app/parcels"], ["Movers & Packers", "/services?travel=logistics&travel_type=movers"]].map(([lbl, route], i) => (
              <button key={i} onClick={() => { setOpen(false); navigate(route); }} data-testid={`sia-chip-${i}`} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold text-slate-600 hover:border-blue-300 hover:text-blue-600">{lbl}</button>
            ))}
            <button onClick={async () => { setOpen(false); try { const r = await api.get("/marketplace/shipping/my-parcels"); const p = (r.data.parcels || [])[0]; navigate(p?.awb ? `/track/${p.awb}` : "/app/parcels"); } catch { navigate("/app/parcels"); } }} data-testid="sia-chip-track-last" className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold text-slate-600 hover:border-blue-300 hover:text-blue-600">Track my last parcel</button>
            {isCustomer && <button onClick={() => { setOpen(false); navigate("/app/shop-orders"); }} data-testid="sia-chip-my-orders" className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold text-slate-600 hover:border-blue-300 hover:text-blue-600">My orders</button>}
            {isCustomer && <button onClick={async () => { setOpen(false); try { const r = await api.get("/marketplace/my-orders"); const o = (r.data.orders || [])[0]; if (o?.order_id) { const rr = await api.post(`/marketplace/orders/${o.order_id}/reorder`); window.dispatchEvent(new Event("cart-updated")); navigate("/app/cart"); } else { navigate("/app/shop-orders"); } } catch { navigate("/app/shop-orders"); } }} data-testid="sia-chip-reorder" className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold text-slate-600 hover:border-blue-300 hover:text-blue-600">Reorder</button>}
          </div>
          <div className="flex items-center gap-2 border-t border-slate-100 p-2.5">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask anything…" data-testid="ai-assistant-input" className="flex-1 rounded-full border border-slate-200 px-3.5 py-2 text-sm outline-none focus:border-blue-300" />
            <button onClick={send} disabled={sending} data-testid="ai-assistant-send" className="flex h-9 w-9 items-center justify-center rounded-full brand-gradient-bg text-white disabled:opacity-50"><Send className="h-4 w-4" /></button>
          </div>
        </div>
      )}
      <button onClick={() => setOpen((o) => !o)} data-testid="ai-assistant-button" data-active-situation={matchedSit?.trigger || "default"} className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-full brand-gradient-bg text-white shadow-xl transition hover:scale-105">
        {launcherImg ? <img src={launcherImg} alt="" data-testid="ai-launcher-avatar" className="h-full w-full object-cover" /> : (() => { const Ic = ICON_MAP[brand?.icon] || MessageCircle; return <Ic className="h-6 w-6" />; })()}
      </button>
    </div>
  );
}
